import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { Check, Star, X } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

function StarRating({ value, onChange, disabled }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-2 select-none" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(n => {
        const active = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            onMouseEnter={() => !disabled && setHover(n)}
            onMouseLeave={() => !disabled && setHover(0)}
            onClick={() => !disabled && onChange(n)}
            disabled={disabled}
            data-testid={`star-${n}`}
            aria-label={`${n} Stern${n > 1 ? 'e' : ''}`}
            className={`w-10 h-10 md:w-12 md:h-12 flex items-center justify-center transition-transform ${
              !disabled ? 'hover:scale-110 active:scale-95' : ''
            }`}
          >
            <Star
              className="w-9 h-9 md:w-11 md:h-11"
              fill={active ? '#EAB308' : 'none'}
              stroke={active ? '#EAB308' : '#9CA3AF'}
              strokeWidth={1.5}
            />
          </button>
        );
      })}
    </div>
  );
}

const LABELS = ['', 'Schlecht', 'Mäßig', 'OK', 'Gut', 'Sehr gut'];

export default function FeedbackPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [googleUrl, setGoogleUrl] = useState(null);

  useEffect(() => {
    api.get(`/feedback/by-token/${token}`)
      .then(r => setData(r.data))
      .catch(() => setError('not_found'));
  }, [token]);

  const submit = async () => {
    if (rating < 1) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/feedback/by-token/${token}/submit`, { rating, comment });
      setGoogleUrl(res.data?.google_review_url || null);
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Fehler beim Absenden');
    } finally {
      setSubmitting(false);
    }
  };

  if (error === 'not_found') {
    return (
      <Wrap>
        <Card>
          <div className="flex flex-col items-center text-center py-8">
            <X className="w-10 h-10 text-[#9CA3AF] mb-3" />
            <p className="text-[#5C5C59]" data-testid="fb-invalid">Feedback-Link ungültig oder abgelaufen.</p>
          </div>
        </Card>
      </Wrap>
    );
  }

  if (!data) {
    return (
      <Wrap>
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#1C1C19] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Wrap>
    );
  }

  const rn = data.logo_text || data.restaurant_name || 'Restaurant';
  const lu = data.logo_url;

  if (data.expired) {
    return (
      <Wrap title={rn} logoUrl={lu}>
        <Card>
          <div className="flex flex-col items-center text-center py-8">
            <X className="w-10 h-10 text-[#9CA3AF] mb-3" />
            <p className="text-[#5C5C59]" data-testid="fb-expired">Dieser Feedback-Link ist leider abgelaufen.</p>
          </div>
        </Card>
      </Wrap>
    );
  }

  if (data.already_submitted || done) {
    return (
      <Wrap title={rn} logoUrl={lu}>
        <Card>
          <div className="flex flex-col items-center text-center py-8" data-testid="fb-thanks">
            <span className="w-14 h-14 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-3">
              <Check className="w-8 h-8 text-[#22C55E]" />
            </span>
            <h2 className="font-display text-2xl text-[#1C1C19] mb-1">Vielen Dank!</h2>
            <p className="text-sm text-[#5C5C59] max-w-sm">
              Wir haben Ihr Feedback erhalten. Ihre Meinung hilft uns, noch besser zu werden.
            </p>
            {googleUrl && (
              <div className="mt-6 w-full max-w-sm bg-[#F9F9F7] border border-[#E5E5E0] rounded-lg p-5">
                <p className="text-sm text-[#1C1C19] mb-3">
                  Hat es Ihnen gefallen? Wir würden uns riesig über eine <strong>Google-Bewertung</strong> freuen!
                </p>
                <a
                  href={googleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="fb-google-review-btn"
                  className="inline-flex items-center justify-center gap-2 w-full px-6 py-3 bg-[#1C1C19] hover:bg-[#3A3A35] text-white rounded text-sm font-medium transition"
                >
                  <Star className="w-4 h-4 fill-current text-[#FCD34D]" /> Auf Google bewerten
                </a>
              </div>
            )}
            <Link to="/" className="mt-6 inline-block px-6 py-2.5 bg-[#1C1C19] hover:bg-[#3A3A35] text-white rounded text-sm" data-testid="fb-home-link">
              Zur Startseite
            </Link>
          </div>
        </Card>
      </Wrap>
    );
  }

  const r = data.request;
  return (
    <Wrap title={rn} logoUrl={lu}>
      <Card>
        <div className="text-center mb-6">
          <h1 className="font-display text-2xl md:text-3xl text-[#1C1C19] mb-2">Wie war Ihr Besuch?</h1>
          {r.date && (
            <p className="text-sm text-[#5C5C59]" data-testid="fb-visit-line">
              Ihr Besuch am {r.date ? new Date(r.date).toLocaleDateString('de-DE') : ''}
              {r.persons ? ` · ${r.persons} Pers.` : ''}
            </p>
          )}
        </div>

        <div className="flex justify-center mb-2">
          <StarRating value={rating} onChange={setRating} disabled={submitting} />
        </div>
        <p className="text-center text-sm text-[#5C5C59] mb-6 h-5" data-testid="fb-rating-label">
          {rating > 0 ? LABELS[rating] : 'Wählen Sie eine Bewertung'}
        </p>

        <div className="mb-4">
          <label className="block text-xs uppercase tracking-wider text-[#5C5C59] mb-1">
            Möchten Sie uns noch etwas mitteilen? <span className="normal-case text-[#9CA3AF]">(optional)</span>
          </label>
          <textarea
            value={comment}
            onChange={e => setComment(e.target.value.slice(0, 2000))}
            data-testid="fb-comment-input"
            rows={4}
            className="w-full px-3 py-2 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:border-[#1C1C19] resize-none"
            placeholder="Was hat Ihnen besonders gefallen? Was können wir besser machen?"
            disabled={submitting}
          />
          <p className="text-[10px] text-right text-[#9CA3AF] mt-0.5">{comment.length}/2000</p>
        </div>

        {error && error !== 'not_found' && (
          <div className="mb-3 px-3 py-2 bg-[#FEE2E2] border border-[#EF4444]/30 rounded text-sm text-[#1C1C19]" data-testid="fb-error">
            {error}
          </div>
        )}

        <button
          onClick={submit}
          disabled={submitting || rating < 1}
          data-testid="fb-submit-btn"
          className="w-full py-3 bg-[#1C1C19] hover:bg-[#3A3A35] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded text-sm font-medium"
        >
          {submitting ? 'Wird gesendet...' : 'Feedback absenden'}
        </button>

        <p className="text-[10px] text-center text-[#9CA3AF] mt-4">
          Ihr Feedback wird vertraulich behandelt und nur intern verwendet.
        </p>
      </Card>
    </Wrap>
  );
}

function Wrap({ title = 'Restaurant', logoUrl, children }) {
  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="feedback-page">
      <div className="max-w-xl mx-auto px-4 md:px-6 py-8 md:py-16">
        <div className="mb-10 flex justify-center">
          <BrandLogo logoUrl={logoUrl} logoText={title} testId="fb-logo" />
        </div>
        {children}
      </div>
    </div>
  );
}

function Card({ children }) {
  return <div className="bg-white border border-[#E5E5E0] rounded-lg p-6 md:p-10">{children}</div>;
}
